import { useTheme } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import useWidth from '../../../../../../../components/Hooks/useWidth';
import useMeasuredWidth from '../../../../../../../hooks/useMeasuredWidth';
import LangEnum from '../../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../../translation/TranslationContext';

export const SUMMARY_COLUMN_GAP_SPACING = 2;

const useSummaryLabel = () => {
  const theme = useTheme();
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();
  const labelRef = useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  const { containerRef: rowRef, width: rowWidth } = useMeasuredWidth();
  const { containerRef: totalsRef, width: totalsWidth } = useMeasuredWidth();

  const fullLabel = translate(LangEnum.GLOBAL_ITEMS_SUMMARY);

  useLayoutEffect(() => {
    const element = labelRef.current;
    const context = document.createElement('canvas').getContext('2d');

    if (mobileWidth || !element || !context) {
      return;
    }

    let isCancelled = false;

    const measure = () => {
      if (isCancelled) {
        return;
      }

      const { fontStyle, fontWeight, fontSize, fontFamily } = getComputedStyle(element);
      context.font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
      setTextWidth(context.measureText(fullLabel).width);
    };

    measure();
    document.fonts?.ready.then(measure);

    return () => {
      isCancelled = true;
    };
  }, [fullLabel, mobileWidth]);

  const columnGap = Number.parseFloat(theme.spacing(SUMMARY_COLUMN_GAP_SPACING));
  const availableWidth = rowWidth - totalsWidth - columnGap;
  const hasRoomForFullLabel = textWidth === 0 || availableWidth >= textWidth;

  const getLabel = () => {
    if (mobileWidth) {
      return null;
    }

    return hasRoomForFullLabel ? fullLabel : translate(LangEnum.GLOBAL_SUMMARY);
  };

  return { rowRef, totalsRef, labelRef, label: getLabel() };
};

export default useSummaryLabel;
